import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import DateRangeFilter, { rangeFor } from "@/components/DateRangeFilter";
import EffBadge from "@/components/EffBadge";
import { fmtNum, fmtPct } from "@/components/KpiCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, FileText, Printer } from "lucide-react";

const REPORTS = [
  { value: "department", label: "Bölüm Verimlilik Raporu" },
  { value: "machine", label: "Makine Verimlilik Raporu" },
  { value: "employee", label: "Personel Verimlilik Raporu" },
  { value: "product", label: "Ürün Performans Raporu" },
  { value: "downtime", label: "Duruş & Fazla Mesai Raporu" },
  { value: "quality", label: "Kalite Raporu" },
  { value: "plan", label: "Plan / Gerçekleşme Raporu" },
];

const REPORT_GROUP = { department: "department", machine: "machine", employee: "employee", product: "product", downtime: "department", quality: "department", plan: "department" };

export default function Reports() {
  const [range, setRange] = useState(() => rangeFor({ key: "1m", months: 1 }));
  const [report, setReport] = useState("department");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ departments: [], machines: [], employees: [], products: [] });

  useEffect(() => { api.get("/meta/options").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    api.get(`/analytics/by?group=${REPORT_GROUP[report]}&start=${range.start}&end=${range.end}`)
      .then((r) => setRows(r.data)).catch(() => {});
  }, [range, report]);

  useEffect(() => { load(); }, [load]);

  const nameOf = (key) => {
    const d = meta.departments.find((x) => x.code === key); if (d) return d.name;
    const m = meta.machines.find((x) => x.code === key); if (m) return `${m.code} — ${m.name}`;
    const e = meta.employees.find((x) => x.code === key); if (e) return e.name;
    const p = meta.products.find((x) => x.code === key); if (p) return `${p.code} — ${p.name}`;
    return key;
  };

  const exportCsv = () => {
    const header = "Kaynak;Standart Kapasite;Plan;Gerçek;Verimlilik %;Plan Gerçekleşme %;Kalite %;Duruş(dk);Fazla Mesai(dk);Fire\n";
    const body = rows.map((r) =>
      [nameOf(r.key), r.standard, r.plan, r.actual, r.efficiency ?? "", r.plan_realization ?? "", r.quality_rate ?? "", r.downtime, r.overtime, r.scrap].join(";")
    ).join("\n");
    const blob = new Blob(["﻿" + header + body], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `rapor_${report}_${range.start}_${range.end}.csv`;
    a.click();
  };

  const exportPdf = async () => {
    try {
      const r = await api.get(`/reports/pdf?group=${REPORT_GROUP[report]}&start=${range.start}&end=${range.end}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `rapor_${report}_${range.start}_${range.end}.pdf`;
      a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="reports-page" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Raporlar</h1>
          <p className="text-sm text-muted-foreground mt-1">{range.start} — {range.end}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={report} onValueChange={setReport}>
            <SelectTrigger className="w-56 h-8 text-xs" data-testid="report-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REPORTS.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`report-type-${r.value}`}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={exportPdf} data-testid="report-export-pdf">
            <FileText size={13} className="mr-1.5" /> PDF İndir
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => window.print()} data-testid="report-print">
            <Printer size={13} className="mr-1.5" /> Yazdır
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={exportCsv} data-testid="report-export-csv">
            <Download size={13} className="mr-1.5" /> CSV İndir
          </Button>
        </div>
      </div>
      <div className="no-print">
        <DateRangeFilter value={range} onChange={setRange} testidPrefix="reports-date" />
      </div>

      <Card className="bg-card border-border overflow-hidden" data-testid="report-table-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              {["Kaynak", "Standart Kapasite", "Plan", "Gerçek", "Verimlilik", "Plan Gerçekleşme", "Kalite", "Duruş (dk)", "Fazla Mesai (dk)", "Fire"].map((h) => (
                <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-right first:text-left">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key} className="border-border" data-testid={`report-row-${r.key}`}>
                <TableCell className="text-sm py-2.5 font-medium">{nameOf(r.key)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono">{fmtNum(r.standard)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono">{fmtNum(r.plan)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono">{fmtNum(r.actual)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right"><EffBadge value={r.efficiency} testid={`report-eff-${r.key}`} /></TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono">{fmtPct(r.plan_realization)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono">{fmtPct(r.quality_rate)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono text-muted-foreground">{fmtNum(r.downtime)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono text-muted-foreground">{fmtNum(r.overtime)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono text-muted-foreground">{fmtNum(r.scrap)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">Bu dönem için veri bulunamadı</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
